import { useEffect } from 'react';

/**
 * containerRef（ポップアップ本体のDOM要素）の外側を押したらonOutsideを呼ぶHook
 * 1) ignoreSelectorsに該当したら「外側判定をしない」で終了（閉じない）
 * 2) containerRefの内側を押したら終了（閉じない）
 * 3) それ以外は外側なのでonOutsideを呼ぶ（閉じる）
 */
export function useOnClickOutside({
  containerRef,
  onOutside,
  enabled = true,
  ignoreSelectors = [],
}) {
  useEffect(() => {
    // ポップアップが閉じている時など、監視が不要な場合は何もしない
    if (!enabled) {
      return;
    }

    // pointerdown を使うことで、マウス/タッチ/ペンを1本化
    const handler = (e) => {
      const containerEl = containerRef?.current;

      // e.targetは「実際に押された要素」
      const target = e.target;
      const targetEl = target instanceof Element ? target : null;

      if (!targetEl) {
        return;
      }
      // containerRefがまだDOMに紐付いていない場合は判定不能なので何もしない
      if (!containerEl) {
        return;
      }

      // 1)ignoreSelectorsの上なら無視（=閉じない）
      for (const selector of ignoreSelectors) {
        // null/空文字などは無視
        if (!selector) {
          continue;
        }
        // そのセレクタに一致する要素の上を押した
        const isIgnoredArea = Boolean(targetEl.closest(selector));
        if (isIgnoredArea) {
          return;
        }
      }

      // 2) ポップアップ内（containerElの内側）なら無視（=閉じない）
      const isInside = containerEl.contains(targetEl);
      if (isInside) {
        return;
      }

      // 3) それ以外は外側なので閉じる
      onOutside?.(e);
    };

    // capture=true で「早めに」拾う（他のクリック処理より先に判定できて安定しやすい）
    document.addEventListener('pointerdown', handler, true);

    return () => {
      document.removeEventListener('pointerdown', handler, true);
    };
  }, [containerRef, onOutside, enabled, ignoreSelectors]);
}